// Battery Diagnostic Engine - Rule-based expert system
// For WFD New Energy - EV Battery Diagnostic Tool

export interface DiagnosticAnswers {
  batteryType: string;
  symptoms: string[];
  voltageReading: string;
  capacityLoss: string;
  vehicleBrand: string;
  vehicleModel: string;
  batteryCapacityKwh: string;
  mileageKm: string;
}

export interface DiagnosticResult {
  summary: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  severityLabel: string;
  severityColor: string;
  recommendedService: string;
  estimatedCostRange: string;
  confidenceScore: number;
  actionItems: string[];
  detailedAnalysis: string;
  possibleCauses: string[];
  benefits: string[];
}

export interface DiagnosticQuestion {
  id: string;
  step: number;
  title: string;
  subtitle: string;
  type: 'single' | 'multiple' | 'text';
  options?: { value: string; label: string; icon?: string }[];
  placeholder?: string;
}

export const diagnosticQuestions: DiagnosticQuestion[] = [
  {
    id: 'batteryType',
    step: 1,
    title: 'What type of EV battery do you work with?',
    subtitle: 'Select the battery chemistry you primarily service',
    type: 'single',
    options: [
      { value: 'lfp', label: 'LFP (Lithium Iron Phosphate)', icon: '🔋' },
      { value: 'nmc', label: 'NMC/NCA (Ternary Lithium)', icon: '⚡' },
      { value: 'blade', label: 'Blade Battery (BYD style)', icon: '🟩' },
      { value: 'other', label: 'Other / Not Sure', icon: '❓' },
    ],
  },
  {
    id: 'symptoms',
    step: 2,
    title: 'What symptoms are you observing?',
    subtitle: 'Select all that apply (multiple choice)',
    type: 'multiple',
    options: [
      { value: 'range_drop', label: 'Significant Range Drop' },
      { value: 'slow_charge', label: 'Slow / Stops Charging' },
      { value: 'cell_imbalance', label: 'Cell Voltage Imbalance' },
      { value: 'thermal_issue', label: 'Overheating Issues' },
      { value: 'bms_error', label: 'BMS Error Codes' },
      { value: 'power_loss', label: 'Power / Acceleration Loss' },
      { value: 'swelling', label: 'Battery Pack Swelling' },
      { value: 'no_start', label: 'Vehicle Won\'t Start' },
    ],
  },
  {
    id: 'voltageReading',
    step: 3,
    title: 'What is the battery pack voltage reading?',
    subtitle: 'Full charge vs current voltage difference',
    type: 'single',
    options: [
      { value: 'normal', label: 'Within normal range (±2%)' },
      { value: 'slight_drop', label: 'Slight drop (3-8% below rated)' },
      { value: 'significant_drop', label: 'Significant drop (8-15% below)' },
      { value: 'severe_drop', label: 'Severe drop (>15% below)' },
      { value: 'unknown', label: 'Not measured yet' },
    ],
  },
  {
    id: 'capacityLoss',
    step: 4,
    title: 'Estimated capacity loss?',
    subtitle: 'Based on real-world range vs original',
    type: 'single',
    options: [
      { value: 'under10', label: 'Under 10% (normal degradation)' },
      { value: '10to20', label: '10-20% (moderate wear)' },
      { value: '20to30', label: '20-30% (significant degradation)' },
      { value: 'over30', label: 'Over 30% (severe degradation)' },
      { value: 'unknown', label: 'Not sure' },
    ],
  },
  {
    id: 'vehicleInfo',
    step: 5,
    title: 'Vehicle & Battery Information',
    subtitle: 'Help us give you a more accurate diagnosis',
    type: 'single', // Using this as step 5 before results - placeholder
    options: [],
  },
];

export function runDiagnosis(answers: DiagnosticAnswers): DiagnosticResult {
  let severityScore = 0;
  const possibleCauses: string[] = [];
  const actionItems: string[] = [];
  const benefits: string[] = [];

  // Symptom-based scoring
  const symptomSeverity: Record<string, number> = {
    range_drop: 2,
    slow_charge: 2,
    cell_imbalance: 3,
    thermal_issue: 4,
    bms_error: 3,
    power_loss: 3,
    swelling: 5,
    no_start: 5,
  };

  const symptomCauses: Record<string, string[]> = {
    range_drop: ['Cell capacity degradation', 'BMS calibration drift', 'High internal resistance'],
    slow_charge: ['BMS communication issue', 'Charging port/cable fault', 'Cell imbalance preventing fast charge'],
    cell_imbalance: ['Uneven cell aging', 'Poor thermal management', 'Manufacturing variance amplified'],
    thermal_issue: ['Cooling system failure', 'Internal short circuit risk', 'Overworked BMS thermal management'],
    bms_error: ['BMS firmware issue', 'Sensor calibration fault', 'CAN bus communication error'],
    power_loss: ['Severe cell degradation', 'High voltage contactor issue', 'Inverter fault'],
    swelling: ['Gas generation from cell failure', 'Severe overcharge history', 'Critical cell fault - fire risk'],
    no_start: ['Main fuse blown', 'Battery management system failure', 'Complete pack voltage below threshold'],
  };

  answers.symptoms.forEach(symptom => {
    severityScore += symptomSeverity[symptom] || 1;
    if (symptomCauses[symptom]) {
      possibleCauses.push(...symptomCauses[symptom]);
    }
  });

  // Voltage-based scoring
  const voltageScore: Record<string, number> = {
    normal: 0,
    slight_drop: 2,
    significant_drop: 4,
    severe_drop: 6,
    unknown: 1,
  };
  severityScore += voltageScore[answers.voltageReading] || 0;

  // Capacity loss scoring
  const capacityScore: Record<string, number> = {
    under10: 1,
    '10to20': 3,
    '20to30': 5,
    over30: 7,
    unknown: 2,
  };
  severityScore += capacityScore[answers.capacityLoss] || 0;

  // Battery type modifier
  if (answers.batteryType === 'lfp' || answers.batteryType === 'blade') {
    severityScore = Math.round(severityScore * 0.9); // LFP degrades slower
  }

  // Determine severity level
  let severity: DiagnosticResult['severity'];
  let severityLabel: string;
  let severityColor: string;
  let summary: string;
  let recommendedService: string;
  let estimatedCostRange: string;

  if (severityScore <= 4) {
    severity = 'low';
    severityLabel = 'Mild Wear';
    severityColor = 'emerald';
    summary = 'Battery is in good condition with normal degradation';
    recommendedService = 'Battery Health Check & BMS Calibration';
    estimatedCostRange = '$150 - $300';
    actionItems.push(
      'Perform full battery health diagnostic scan',
      'Run BMS re-calibration procedure',
      'Check cooling system operation',
      'Set baseline for future comparison'
    );
    benefits.push('Prevent future failures', 'Extend battery life by 20-30%', 'Peace of mind for fleet operations');
  } else if (severityScore <= 8) {
    severity = 'medium';
    severityLabel = 'Moderate Degradation';
    severityColor = 'yellow';
    summary = 'Moderate battery degradation detected - maintenance recommended';
    recommendedService = 'Cell Balancing + Capacity Recovery Service';
    estimatedCostRange = '$300 - $800';
    actionItems.push(
      'Complete cell voltage balance service',
      'Deep capacity test for accurate SOH',
      'Clean and inspect high-voltage connections',
      'BMS firmware update if applicable',
      'Cooling system flush and inspection'
    );
    benefits.push('Restore 10-20% lost capacity', 'Improve charging speed', 'Extend remaining life significantly', 'Avoid more costly replacement');
  } else if (severityScore <= 14) {
    severity = 'high';
    severityLabel = 'Significant Degradation';
    severityColor = 'orange';
    summary = 'Significant battery degradation - professional repair needed soon';
    recommendedService = 'Full Battery Rebuild / Module Replacement';
    estimatedCostRange = '$1,500 - $4,000';
    actionItems.push(
      'Full pack disassembly and cell/module testing',
      'Replace degraded cells or modules',
      'Reassemble with proper torque and insulation',
      'Complete BMS reconfiguration',
      'High-voltage insulation test',
      'Full capacity verification test'
    );
    benefits.push('Save 50-70% vs new battery pack', 'Restore 90%+ of original capacity', '2-year warranty on repair', 'Environmentally sustainable');
  } else {
    severity = 'critical';
    severityLabel = 'Critical Condition';
    severityColor = 'red';
    summary = 'CRITICAL - Stop using immediately! Severe battery failure detected';
    recommendedService = 'Emergency Battery Repair / Full Rebuild';
    estimatedCostRange = '$2,500 - $6,000';
    actionItems.push(
      '⚠️ Disconnect vehicle from power immediately',
      'Move to well-ventilated fire-safe area',
      'Emergency diagnostic and fault identification',
      'Replace all faulty cells/modules',
      'Thermal management system overhaul',
      'Complete safety verification testing',
      'BMS full replacement if needed'
    );
    benefits.push('Critical safety risk eliminated', 'Avoid catastrophic battery failure', 'Save vs full replacement cost', 'Get vehicle back on road fast');
  }

  // Deduplicate causes
  const uniqueCauses = [...new Set(possibleCauses)].slice(0, 6);

  // Confidence scoring
  let confidence = 60; // base
  if (answers.voltageReading !== 'unknown') confidence += 10;
  if (answers.capacityLoss !== 'unknown') confidence += 10;
  if (answers.symptoms.length >= 3) confidence += 10;
  if (answers.symptoms.length >= 5) confidence += 5;
  confidence = Math.min(95, confidence);

  // Detailed analysis
  const detailedAnalysis = generateDetailedAnalysis(answers, severity, uniqueCauses);

  return {
    summary,
    severity,
    severityLabel,
    severityColor,
    recommendedService,
    estimatedCostRange,
    confidenceScore: confidence,
    actionItems,
    detailedAnalysis,
    possibleCauses: uniqueCauses,
    benefits,
  };
}

function generateDetailedAnalysis(
  answers: DiagnosticAnswers,
  severity: string,
  causes: string[]
): string {
  const batteryNames: Record<string, string> = {
    lfp: 'LFP (Lithium Iron Phosphate)',
    nmc: 'NMC/NCA Ternary Lithium',
    blade: 'Blade Battery',
    other: 'your battery type',
  };

  const batteryName = batteryNames[answers.batteryType] || 'your battery';

  let analysis = `Based on your reported ${answers.symptoms.length} symptom${answers.symptoms.length > 1 ? 's' : ''} `;
  analysis += `from a ${batteryName} pack, `;

  if (severity === 'low') {
    analysis += `your battery appears to be in good overall health. The degradation pattern is consistent with normal usage wear. `;
    analysis += `A professional health check and BMS calibration can help optimize performance and establish a baseline for future monitoring.`;
  } else if (severity === 'medium') {
    analysis += `your battery is showing moderate degradation that is beyond normal wear. `;
    analysis += `Cell imbalance and capacity fade are likely the primary issues. `;
    analysis += `Professional cell balancing service can typically restore 10-20% of lost capacity and significantly improve charging performance.`;
  } else if (severity === 'high') {
    analysis += `your battery has significant degradation that requires professional attention. `;
    analysis += `Multiple subsystems appear affected. Module-level repair or partial rebuild is recommended. `;
    analysis += `Addressing this now can prevent further damage and save you from the cost of a full pack replacement.`;
  } else {
    analysis += `your battery is in CRITICAL condition. STOP using the vehicle immediately and have it inspected by professionals. `;
    analysis += `The combination of symptoms indicates severe cell failure with potential safety risks including thermal runaway. `;
    analysis += `Emergency service is required to assess and repair the battery safely.`;
  }

  if (causes.length > 0) {
    analysis += `\n\nLikely contributing factors include: ${causes.slice(0, 3).join(', ')}.`;
  }

  return analysis;
}

// Helper: format severity for display
export function getSeverityStyles(severity: string) {
  const styles: Record<string, { bg: string; text: string; border: string; glow: string; dot: string }> = {
    low: {
      bg: 'bg-emerald-500/10',
      text: 'text-emerald-400',
      border: 'border-emerald-500/30',
      glow: 'shadow-[0_0_20px_rgba(16,185,129,0.3)]',
      dot: 'bg-emerald-400',
    },
    medium: {
      bg: 'bg-yellow-500/10',
      text: 'text-yellow-400',
      border: 'border-yellow-500/30',
      glow: 'shadow-[0_0_20px_rgba(234,179,8,0.3)]',
      dot: 'bg-yellow-400',
    },
    high: {
      bg: 'bg-orange-500/10',
      text: 'text-orange-400',
      border: 'border-orange-500/30',
      glow: 'shadow-[0_0_20px_rgba(249,115,22,0.3)]',
      dot: 'bg-orange-400',
    },
    critical: {
      bg: 'bg-red-500/10',
      text: 'text-red-400',
      border: 'border-red-500/30',
      glow: 'shadow-[0_0_30px_rgba(239,68,68,0.4)]',
      dot: 'bg-red-400',
    },
  };
  return styles[severity] || styles.medium;
}
